import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { LogoMark } from "../Logo";
import { SUPPORT_EMAIL as CONTACT } from "../contact";
const UPDATED = "September 3, 2026";

function LegalLayout({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <>
      <header className="site-header">
        <div className="inner">
          <Link to="/" className="brand">
            <span className="mark">
              <LogoMark size={18} />
            </span>
            <span>Caldwell School Lunch</span>
          </Link>
        </div>
      </header>
      <main className="app-main legal">
        <h1>{title}</h1>
        <p className="muted" style={{ marginTop: 4 }}>
          Last updated {UPDATED}
        </p>
        {children}
        <p style={{ marginTop: 32 }}>
          <Link to="/">← Back to Caldwell School Lunch</Link>
        </p>
      </main>
    </>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        Caldwell School Lunch (&ldquo;the app&rdquo;) is a small personal tool that turns a
        Google&nbsp;Forms school-lunch order you forward by email into events on a
        Google Calendar you choose. This policy explains what data it uses and
        why.
      </p>

      <h2>Information the app accesses</h2>
      <ul>
        <li>
          <b>Google account basics</b> &mdash; your name, email address, and
          profile picture, used to identify your account and show who is signed
          in.
        </li>
        <li>
          <b>Google Calendar</b> &mdash; the list of your calendars (so you can
          pick one), and permission to create, update, and delete calendar
          events. The app only modifies events it created (each is tagged with a
          private property); it does not read or change your other events.
        </li>
        <li>
          <b>Emails you forward</b> &mdash; when you forward a Google&nbsp;Forms
          &ldquo;response receipt&rdquo; to the app&rsquo;s dedicated address, the
          message is delivered to the app (via Cloudflare Email Routing) and the
          order is extracted from it.
        </li>
        <li>
          <b>Menu PDFs you upload</b> &mdash; the monthly lunch menu file and the
          dish text extracted from it.
        </li>
      </ul>

      <h2>Information the app stores</h2>
      <ul>
        <li>Your Google account ID, email, name, and profile-picture URL.</li>
        <li>
          Your Google OAuth <b>refresh token</b>, encrypted at rest with
          AES-256-GCM, so the app can act on the calendar you authorized.
        </li>
        <li>
          The <b>child rules</b> you configure: a student first name, optional
          grade and school, the target calendar, the event title template,
          suffix, start time, and duration.
        </li>
        <li>
          Parsed <b>lunch orders</b>: student first/last name, grade, school,
          milk selection, and the per-day choice from the form, plus the plain
          text of the forwarded email (kept so an order can be re-checked).
        </li>
        <li>
          Uploaded menu files and the parsed per-day dish data, and a local
          record of the calendar events the app has created for you.
        </li>
        <li>
          Message identifiers of emails already processed, to avoid handling the
          same forward twice.
        </li>
      </ul>

      <h2>How the information is used</h2>
      <p>
        Data is used solely to provide the feature you asked for: reading a
        forwarded order and creating or updating the corresponding calendar
        events, and displaying your orders and menus back to you. It is not used
        for advertising or profiling.
      </p>

      <h2>Third parties</h2>
      <ul>
        <li>
          <b>Google</b> &mdash; the app calls Google APIs to read your profile
          and manage the calendar events you request.
        </li>
        <li>
          <b>Anthropic</b> &mdash; if menu-text assistance is enabled by the
          operator, the text extracted from an uploaded menu PDF is sent to
          Anthropic&rsquo;s Claude (via a local command-line tool) to clean it
          into structured dish names. Menu text contains dish names only, not
          personal data. This can be turned off, in which case a built-in parser
          is used instead.
        </li>
      </ul>
      <p>
        The app does not sell your data and does not share it with anyone else.
      </p>

      <h2>Google user data</h2>
      <p>
        Caldwell School Lunch&rsquo;s use and transfer of information received from Google
        APIs adheres to the{" "}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Google Calendar and profile
        data are used only to provide user-facing features described here, are
        not transferred to others except as needed to provide those features, and
        are not used for ads.
      </p>

      <h2>Retention and deletion</h2>
      <p>
        Orders and events remain until you remove them. Deleting a child rule or
        an order in the app removes its stored records; using &ldquo;Remove all
        events&rdquo; deletes the events the app created from your calendar. To
        delete your account and all associated data, or to revoke access, email{" "}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>, or remove the app at{" "}
        <a
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noreferrer"
        >
          myaccount.google.com/permissions
        </a>
        .
      </p>

      <h2>Security</h2>
      <p>
        Traffic is served over HTTPS. Refresh tokens are encrypted at rest. The
        app runs on a private server and access is limited to signed-in users
        acting on their own data.
      </p>

      <h2>Children</h2>
      <p>
        The app is used by parents/guardians. It stores a child&rsquo;s first
        name (and optionally last name, grade, and school) that you enter or that
        appears in the order you forward. It is not directed to children and
        children do not use it.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy changes, the &ldquo;last updated&rdquo; date above will
        change. Contact: <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </p>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service">
      <p>
        By using Caldwell School Lunch (&ldquo;the app&rdquo;) you agree to these terms.
      </p>

      <h2>What the app does</h2>
      <p>
        The app reads a Google&nbsp;Forms lunch-order receipt you forward to it
        and creates matching events on a Google Calendar you choose. It is a
        convenience tool for viewing and scheduling; it does <b>not</b> place or
        change the actual lunch order with your school or its lunch provider, and
        it does not guarantee that an order was received or is correct. Always
        confirm orders through the school&rsquo;s official process.
      </p>

      <h2>Your responsibilities</h2>
      <ul>
        <li>
          Only forward receipts for your own household and only connect calendars
          you are allowed to manage.
        </li>
        <li>
          Keep your Google account secure. You are responsible for activity under
          your sign-in.
        </li>
        <li>
          Review the events the app creates. Menu text extraction can be
          imperfect; correct it where needed.
        </li>
      </ul>

      <h2>No affiliation</h2>
      <p>
        Caldwell School Lunch is an independent personal project. It is not affiliated
        with, endorsed by, or sponsored by any school or school district,
        Nutrislice, Google, or Anthropic. Product names are the property of their
        respective owners.
      </p>

      <h2>Availability</h2>
      <p>
        The app is provided on a best-effort basis and may change, break, or be
        discontinued at any time without notice.
      </p>

      <h2>Disclaimer and limitation of liability</h2>
      <p>
        The app is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;,
        without warranties of any kind, express or implied, including
        merchantability, fitness for a particular purpose, and non-infringement.
        To the fullest extent permitted by law, the operator is not liable for
        any indirect, incidental, or consequential damages, or for missed meals,
        incorrect orders, missed or duplicated calendar events, or data loss
        arising from use of the app.
      </p>

      <h2>Termination</h2>
      <p>
        You may stop using the app at any time and revoke its access from your
        Google account. The operator may suspend access to protect the service.
      </p>

      <h2>Governing law</h2>
      <p>
        These terms are governed by the laws of the State of New Jersey, USA,
        without regard to conflict-of-law rules.
      </p>

      <h2>Contact</h2>
      <p>
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
      </p>
    </LegalLayout>
  );
}
